/**
 * Loads the page data from the website API.
 *
 * @returns {Object} Containing the page and the fetchPage method.
 */
export const usePage = () => {
	const page = useState('page', () => {});
	const { locale, defaultLocale } = useI18n();

	/**
	 * Sets the page state.
	 *
	 * @param {*} data The page data.
	 *
	 * @returns {void} Nothing.
	 */
	const setPage = (data) => {
		page.value = data;
	};

	/**
	 * Fetches the page data from the API and sets it in the page state.
	 *
	 * @returns {void} Nothing.
	 */
	const fetchPage = async () => {
		const route = useRoute();
		const pageFilename = useFilename(route, locale.value);
		const key = `page${route.path}`;

		try {
			if (pathContainsDefaultLocale(route)) {
				// The default locale is served on the root path, so if the route path contains the default locale,
				// we show a 404 page.
				return setPage(await fetch404Page());
			}

			const { data: pageData } = await useWebsiteFetch('pages', {
				query: { filename: pageFilename, language: locale.value },
				key,
			});

			// No page found, so fetch the 404 page
			if (!pageData.value || !Object.keys(pageData.value).length) {
				return setPage(await fetch404Page());
			}

			return setPage(pageData);
		} catch (_error) {
			// eslint-disable-next-line no-console
			console.error('Error fetching page:', key);

			return setPage([]);
		}
	};

	/**
	 * Fetches the 404 page from the API.
	 *
	 * @param {String} key The key to use for the cache.
	 *
	 * @returns {Promise<Object>} The page data.
	 */
	const fetch404Page = async () => {
		const fallback = { title: 'Page not found', content: 'The page you are looking for does not exist.' };

		try {
			const key = locale.value + '/404';
			const { data: result } = await useWebsiteFetch('pages', {
				query: { filename: '404', language: locale.value },
				key,
			});

			const event = useRequestEvent();
			setResponseStatus(event, 404);

			return result?.value || fallback;
		} catch (_error) {
			// eslint-disable-next-line no-console
			console.error('Error fetching 404 page:', key);

			return fallback;
		}
	};

	/**
	 * Checks if the route path contains the default locale.
	 *
	 * @param {Object} route The route object
	 *
	 * @returns {Boolean} Whether or not the route path contains the default locale.
	 */
	const pathContainsDefaultLocale = (route) => {
		if (route.path === `/${defaultLocale}` || route.path.startsWith(`/${defaultLocale}/`)) {
			return true;
		}

		return false;
	};

	return { page, fetchPage };
};
